import React from 'react';

/**
 * VehicleStats Component
 * Displays statistics cards for vehicles overview.
 * Uses quickStats (from fast /count endpoint) when available,
 * falls back to local JS filtering from the vehicles array.
 */
export default function VehicleStats({ vehicles, quickStats, statsLoading, t }) {
  // Prefer server-side counts (instant, no JOIN), fall back to local array
  const stats = quickStats
    ? {
        total:       parseInt(quickStats.total)       || 0,
        active:      parseInt(quickStats.active)      || 0,
        inactive:    parseInt(quickStats.inactive)    || 0,
        mifare:      parseInt(quickStats.mifare_count) || 0,
        obu_sbu:     parseInt(quickStats.obu_count)   || 0,
        // These need full data — compute locally once vehicles arrive
        vip:         vehicles.filter(v => v.customer_type === 'VIP').length,
        normal:      vehicles.filter(v => v.customer_type === 'Normal').length,
        suspended:   vehicles.filter(v => v.status === 'suspended').length,
        maintenance: vehicles.filter(v => v.status === 'maintenance').length,
      }
    : {
        total:       vehicles.length,
        vip:         vehicles.filter(v => v.customer_type === 'VIP').length,
        normal:      vehicles.filter(v => v.customer_type === 'Normal').length,
        mifare:      vehicles.filter(v => v.device_type === 'mifare').length,
        obu_sbu:     vehicles.filter(v => v.device_type === 'obu_sbu').length,
        active:      vehicles.filter(v => v.status === 'active').length,
        inactive:    vehicles.filter(v => v.status === 'inactive').length,
        suspended:   vehicles.filter(v => v.status === 'suspended').length,
        maintenance: vehicles.filter(v => v.status === 'maintenance').length,
      };

  // Skeleton card for loading state
  const SkeletonCard = () => (
    <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg animate-pulse">
      <div className="h-8 w-12 bg-gray-300 dark:bg-gray-500 rounded mb-2" />
      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded" />
    </div>
  );

  if (statsLoading && !quickStats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[...Array(9)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      {/* Total Vehicles */}
      <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
          {stats.total}
        </div>
        <div className="text-blue-600 dark:text-blue-400 text-sm">
          🚗 {t('vehicles.statistics.total_vehicles')}
        </div>
      </div>
      
      {/* VIP Customers */}
      <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
          {stats.vip}
        </div>
        <div className="text-yellow-600 dark:text-yellow-400 text-sm">⭐ VIP</div>
      </div>
      
      {/* Normal Customers */}
      <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
        <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">
          {stats.normal}
        </div>
        <div className="text-gray-600 dark:text-gray-400 text-sm">👤 Normal</div>
      </div>
      
      {/* Mifare Devices */}
      <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-green-800 dark:text-green-200">
          {stats.mifare}
        </div>
        <div className="text-green-600 dark:text-green-400 text-sm">💳 Mifare</div>
      </div>
      
      {/* OBU-SBU Devices */}
      <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
          {stats.obu_sbu}
        </div>
        <div className="text-purple-600 dark:text-purple-400 text-sm">📡 OBU-SBU</div>
      </div>

      {/* Active Status */}
      <div className="bg-emerald-100 dark:bg-emerald-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
          {stats.active}
        </div>
        <div className="text-emerald-600 dark:text-emerald-400 text-sm">🟢 Active</div>
      </div>

      {/* Inactive Status */}
      <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-red-800 dark:text-red-200">
          {stats.inactive}
        </div>
        <div className="text-red-600 dark:text-red-400 text-sm">🔴 Inactive</div>
      </div>

      {/* Suspended Status */}
      <div className="bg-orange-100 dark:bg-orange-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-orange-800 dark:text-orange-200">
          {stats.suspended}
        </div>
        <div className="text-orange-600 dark:text-orange-400 text-sm">⚠️ Suspended</div>
      </div>

      {/* Maintenance Status */}
      <div className="bg-cyan-100 dark:bg-cyan-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-cyan-800 dark:text-cyan-200">
          {stats.maintenance}
        </div>
        <div className="text-cyan-600 dark:text-cyan-400 text-sm">🔧 Maintenance</div>
      </div>

      {/* Completion Rate */}
      <div className="bg-indigo-100 dark:bg-indigo-900 p-4 rounded-lg">
        <div className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">
          {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
        </div>
        <div className="text-indigo-600 dark:text-indigo-400 text-sm">📊 Active Rate</div>
      </div>
    </div>
  );
}
